import React, { useEffect, useMemo } from 'react';
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  Tooltip,
  Polyline,
  Polygon,
  Circle,
  useMap,
} from 'react-leaflet';
import L from 'leaflet';
import {
  USA_CENTER,
  USA_ZOOM,
  getSegmentColor,
  toLeafletPath,
} from './osrmRoute';
import { buildMarkerHtml } from './mapMarkerHtml';
import {
  MarkerPopupBody,
  buildZoneViewMarkers,
} from './mapShared';
import { DEFAULT_BASEMAP } from './mapBasemaps';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

function makeDivIcon(markerHtml) {
  return L.divIcon({
    className: '',
    html: markerHtml.html,
    iconSize: [markerHtml.width, markerHtml.height],
    iconAnchor: [markerHtml.width / 2, markerHtml.height / 2],
  });
}

function MapViewController({ bounds, selectedDriver, viewMode, driverZones }) {
  const map = useMap();

  useEffect(() => {
    if (viewMode === 'zone' && driverZones?.length > 0) {
      const allPoints = [];
      driverZones.forEach((zone) => {
        if (zone.isPolygon && zone.polygon) {
          allPoints.push(...zone.polygon);
        } else if (zone.center) {
          allPoints.push(zone.center);
        }
      });
      if (allPoints.length > 0) {
        map.fitBounds(L.latLngBounds(allPoints), { padding: [50, 50] });
      }
      return;
    }

    if (selectedDriver && bounds?.length > 0) {
      map.fitBounds(L.latLngBounds(bounds), { padding: [50, 50] });
      return;
    }

    map.setView(USA_CENTER, USA_ZOOM);
  }, [map, bounds, selectedDriver, viewMode, driverZones]);

  return null;
}

function LeafletMapContent({
  routeData,
  routes,
  formatTime,
  viewMode,
  driverZones,
  selectedDriver,
  driversWithTrips,
  visibleDriverZones,
  selectedTripId,
}) {
  const routeMarkers = useMemo(() => {
    if (viewMode !== 'route' || !routeData?.markers?.length) return [];
    return routeData.markers.map((marker, index) => ({
      ...marker,
      key: `route-${index}`,
      markerHtml: buildMarkerHtml(marker, { selectedTripId }),
    }));
  }, [viewMode, routeData, selectedTripId]);

  const zoneMarkers = useMemo(() => {
    if (viewMode !== 'zone') return [];
    const raw = buildZoneViewMarkers(driversWithTrips, visibleDriverZones);
    return raw.map((marker, index) => {
      const pulsate =
        selectedDriver && marker.driverName === selectedDriver.driver_name;
      return {
        ...marker,
        key: `zone-${index}`,
        markerHtml: buildMarkerHtml(marker, { pulsate }),
      };
    });
  }, [viewMode, driversWithTrips, visibleDriverZones, selectedDriver]);

  const markersToRender = viewMode === 'route' ? routeMarkers : zoneMarkers;

  return (
    <>
      {viewMode === 'route' &&
        routes?.map((segment, index) => (
          <Polyline
            key={`route-segment-${index}`}
            positions={toLeafletPath(segment.coordinates)}
            color={getSegmentColor(segment.segmentType)}
            weight={5}
            opacity={0.85}
            dashArray={segment.segmentType === 'deadhead' ? '10, 10' : null}
          />
        ))}

      {viewMode === 'zone' &&
        driverZones
          ?.filter((zone) => visibleDriverZones.includes(zone.driverId))
          .map((zone) => {
            const isSelected = selectedDriver?.driver_id === zone.driverId;
            const baseOpacity = isSelected ? 0.35 : 0.12;
            const zoneColor = zone.color;

            if (zone.isPolygon && zone.polygon?.length >= 3) {
              return (
                <Polygon
                  key={`zone-poly-${zone.driverId}`}
                  positions={zone.polygon}
                  color={zoneColor}
                  fillColor={zoneColor}
                  fillOpacity={baseOpacity}
                  weight={isSelected ? 4 : 1}
                />
              );
            }
            if (zone.center) {
              return (
                <Circle
                  key={`zone-circle-${zone.driverId}`}
                  center={zone.center}
                  radius={zone.radius}
                  color={zoneColor}
                  fillColor={zoneColor}
                  fillOpacity={baseOpacity}
                  weight={isSelected ? 4 : 1}
                />
              );
            }
            return null;
          })}

      {markersToRender.map((marker) => (
        <Marker
          key={marker.key}
          position={marker.position}
          icon={makeDivIcon(marker.markerHtml)}
        >
          <Popup>
            <MarkerPopupBody marker={marker} formatTime={formatTime} />
          </Popup>
          <Tooltip>{marker.label}</Tooltip>
        </Marker>
      ))}
    </>
  );
}

export default function PlannerLeafletMap({
  routeData,
  routes,
  formatTime,
  viewMode,
  driverZones,
  selectedDriver,
  driversWithTrips,
  visibleDriverZones,
  selectedTripId,
}) {
  return (
    <MapContainer
      center={USA_CENTER}
      zoom={USA_ZOOM}
      className="h-full w-full"
      style={{ height: '100%', width: '100%' }}
    >
      <TileLayer attribution={DEFAULT_BASEMAP.attribution} url={DEFAULT_BASEMAP.url} />
      {DEFAULT_BASEMAP.labels && (
        <TileLayer
          attribution={DEFAULT_BASEMAP.labels.attribution}
          url={DEFAULT_BASEMAP.labels.url}
        />
      )}
      <MapViewController
        bounds={routeData?.bounds}
        selectedDriver={selectedDriver}
        viewMode={viewMode}
        driverZones={driverZones}
      />
      <LeafletMapContent
        routeData={routeData}
        routes={routes}
        formatTime={formatTime}
        viewMode={viewMode}
        driverZones={driverZones}
        selectedDriver={selectedDriver}
        driversWithTrips={driversWithTrips}
        visibleDriverZones={visibleDriverZones}
        selectedTripId={selectedTripId}
      />
    </MapContainer>
  );
}
